import React, { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { Screen } from '../../design/components';
import useBackLabel from '../../hooks/useBackLabel';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import DriverRouteMap, { buildRoutePins } from './DriverRouteMap';

/**
 * Full-screen route map: numbered pins only; tapping a pin shows its site card
 * over the map (SPEC decision 15). Route params: { points } (routeMapPoints).
 */
const DriverRouteMapView = () => {
	const route = useRoute();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const points = route.params?.points;
	const [selectedPinId, setSelectedPinId] = useState(null);
	const pins = useMemo(() => buildRoutePins(points), [points]);
	const selectedPin = pins.find(pin => pin.id === selectedPinId) || null;
	const backLabel = useBackLabel();

	return (
		<Screen
			backLabel={backLabel}
			heading="Route map"
			subheading="Tap a number to see the site."
			scroll={false}
		>
			<View style={styles.mapArea}>
				<DriverRouteMap
					points={points || []}
					style={styles.map}
					selectedPinId={selectedPinId}
					onSelectPin={setSelectedPinId}
				/>
				{selectedPin && (
					<View style={styles.siteCard} accessible={true} accessibilityLiveRegion="polite">
						<View style={styles.pinChip}>
							<Text style={styles.pinChipText}>{selectedPin.label}</Text>
						</View>
						<View style={styles.siteText}>
							<Text style={styles.siteName}>{selectedPin.name}</Text>
							{Boolean(selectedPin.subtitle) && <Text style={styles.siteSubtitle}>{selectedPin.subtitle}</Text>}
						</View>
						<Pressable
							onPress={() => setSelectedPinId(null)}
							accessibilityRole="button"
							accessibilityLabel="Close"
							style={({ pressed }) => [styles.close, pressed && styles.pressed]}
						>
							<Feather name="x" size={20} color={colors.icon} />
						</Pressable>
					</View>
				)}
			</View>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, elevation, radius, space, touch, type } }) => ({
	mapArea: {
		flex: 1,
		paddingHorizontal: space.gutter,
		paddingBottom: space.gutter,
	},
	map: {
		flex: 1,
	},
	siteCard: {
		position: 'absolute',
		top: space.md,
		left: space.gutter + space.md,
		right: space.gutter + space.md,
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		paddingVertical: space.md,
		paddingLeft: 14,
		paddingRight: space.sm,
		borderRadius: radius.hero,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		backgroundColor: colors.surface,
		...elevation.floating,
	},
	pinChip: {
		minWidth: touch.min,
		height: 32,
		paddingHorizontal: space.sm,
		borderRadius: radius.pill,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: colors.primary,
	},
	pinChipText: {
		...type.secondarySemibold,
		color: colors.onPrimary,
	},
	siteText: {
		flex: 1,
		gap: 2,
	},
	siteName: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	siteSubtitle: {
		...type.caption,
		color: colors.textSecondary,
	},
	close: {
		width: touch.min,
		height: touch.min,
		borderRadius: radius.pill,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: colors.raised,
	},
	pressed: {
		opacity: 0.7,
	},
});

export default DriverRouteMapView;
